// The formatting bar over selected text, and the link card at a link the caret
// touches.
//
// Promise:
// - It never takes the focus: buttons act on mousedown-prevented clicks, so the
//   body keeps its selection and focus. Only the link field (and the "type"
//   menu) take it, and give it back to the body when they close.
// - Not shown while the mouse is still selecting (it would jump with the
//   selection's top on every move); shown on release. A keyboard selection shows
//   it at once.
// - Not shown for an empty selection, a whole-block selection (an image, table
//   cells: they have their own menus), only spaces, or text in code.
// - Placed above the visible part of the selection (below when there is no
//   room), aligned to its start (centered, it jumped left when the link field
//   replaced the buttons), and never above the top of a scroll box whose content
//   is scrolled away. Hidden when the selection scrolls out of sight. On a phone
//   it sits at the bottom of the visible screen, centered, above the Dock (an
//   app's too): iOS puts its own menu above or below the selection, and the bar
//   under a finger lost taps to it.
// - Link: the button opens a field; Enter sets the link (a scheme is added when
//   none was typed: `example.com` means https), an empty field removes it, Escape
//   goes back to the text. "Remove link" removes every link the selection
//   touches, end to end (a double click selects the trailing space too, and the
//   link must still come off).
// - The link card appears when the caret is inside a link, or touches its edge
//   after a click (not while typing past it): the URL can be edited in place,
//   opened, or removed.
// - Pressed state is the background and the text color, never bold.
import { type Editor, getMarkRange, posToDOMRect } from "@tiptap/core";
import { useEditorState } from "@tiptap/react";
import { m } from "@trameria/i18n";
import {
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { isMac, keyLabel } from "../../shell/shortcuts";
import { Icon } from "../../ui/icon";
import { isImeComposing } from "../../ui/ime";
import { Tooltip } from "../../ui/tooltip";
import { FloatingMenu } from "../blocks/floating-menu";
import { POPUP_PANEL } from "./suggest";
import { canEdit, isReading, touchFirst } from "./tap-to-type";

const TEXT_TYPES = [
  {
    id: "paragraph",
    label: () => m.documents_block_paragraph(),
    name: "paragraph",
    level: undefined,
  },
  {
    id: "heading1",
    label: () => m.documents_block_heading1(),
    name: "heading",
    level: 1,
  },
  {
    id: "heading2",
    label: () => m.documents_block_heading2(),
    name: "heading",
    level: 2,
  },
  {
    id: "heading3",
    label: () => m.documents_block_heading3(),
    name: "heading",
    level: 3,
  },
] as const;
type TextTypeId = (typeof TEXT_TYPES)[number]["id"];

export interface SelectionMenuState {
  bold: boolean;
  italic: boolean;
  strike: boolean;
  code: boolean;
  /** Some of the selection is a link (not all of it). */
  link: boolean;
  /**
   * The block type, when the selection is in one top-level paragraph or
   * heading type. null otherwise (lists and quotes cannot be turned with
   * `setNode`; the block menu says why).
   */
  textType: TextTypeId | null;
}

/** The bar's state for the selection, or null when no bar is shown. */
export function selectionMenuState(editor: Editor): SelectionMenuState | null {
  if (!canEdit(editor)) return null;
  const { state } = editor;
  const { selection } = state;
  if (selection.empty) return null;
  const type = (selection.toJSON() as { type?: string }).type;
  if (type !== "text" && type !== "all") return null;
  const { from, to, $from, $to } = selection;
  if (state.doc.textBetween(from, to, " ", " ").trim() === "") return null;
  if ($from.parent.type.spec.code || $to.parent.type.spec.code) return null;
  return {
    bold: editor.isActive("bold"),
    italic: editor.isActive("italic"),
    strike: editor.isActive("strike"),
    code: editor.isActive("code"),
    link: linkRange(editor) !== null,
    textType: textTypeOf(editor),
  };
}

function textTypeOf(editor: Editor): TextTypeId | null {
  const { $from, $to } = editor.state.selection;
  if ($from.depth !== 1 || $to.depth !== 1) return null;
  const a = $from.parent;
  const b = $to.parent;
  if (a.type !== b.type || a.attrs.level !== b.attrs.level) return null;
  if (a.type.name === "paragraph") return "paragraph";
  if (a.type.name === "heading")
    return TEXT_TYPES.find((t) => t.level === a.attrs.level)?.id ?? null;
  return null;
}

/** The links the selection touches, widened to their ends, and the first href. */
export function linkRange(
  editor: Editor,
): { from: number; to: number; href: string } | null {
  const type = editor.schema.marks.link;
  if (!type) return null;
  const { doc, selection } = editor.state;
  let first: number | null = null;
  let last: number | null = null;
  let href = "";
  doc.nodesBetween(selection.from, selection.to, (node, pos) => {
    if (!node.isText) return;
    const mark = type.isInSet(node.marks);
    if (!mark) return;
    if (first === null) {
      first = pos;
      href = String(mark.attrs.href ?? "");
    }
    last = pos;
  });
  if (first === null || last === null) return null;
  const head = getMarkRange(doc.resolve(first), type);
  const tail = getMarkRange(doc.resolve(last), type);
  return {
    from: Math.min(head?.from ?? first, selection.from),
    to: Math.max(tail?.to ?? last, selection.to),
    href,
  };
}

function removeLinkRange(
  editor: Editor,
  range: { from: number; to: number },
): boolean {
  const type = editor.schema.marks.link;
  if (!type) return false;
  return editor
    .chain()
    .focus()
    .command(({ tr }) => {
      tr.removeMark(range.from, range.to, type);
      return true;
    })
    .run();
}

export function removeLinks(editor: Editor): boolean {
  const range = linkRange(editor);
  return range ? removeLinkRange(editor, range) : false;
}

export interface CaretLink {
  from: number;
  to: number;
  href: string;
  /** The caret is inside the link (not only at its edge). */
  inside: boolean;
}

/** The link an empty selection touches (for the link card). */
export function caretLink(editor: Editor): CaretLink | null {
  if (!canEdit(editor)) return null;
  const type = editor.schema.marks.link;
  const { selection } = editor.state;
  if (!type || !selection.empty) return null;
  const { $from } = selection;
  const before = $from.nodeBefore
    ? type.isInSet($from.nodeBefore.marks)
    : undefined;
  const after = $from.nodeAfter
    ? type.isInSet($from.nodeAfter.marks)
    : undefined;
  const mark = after ?? before;
  if (!mark) return null;
  const at = after ? $from : editor.state.doc.resolve($from.pos - 1);
  const range = getMarkRange(at, type, mark.attrs);
  if (!range) return null;
  return {
    from: range.from,
    to: range.to,
    href: String(mark.attrs.href ?? ""),
    inside: before !== undefined && after !== undefined && before.eq(after),
  };
}

/** The text of the link field as an href; null removes the link. */
export function normalizeHref(input: string): string | null {
  const value = input.trim();
  if (value === "") return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value) || /^[/#?]/.test(value)) return value;
  return `https://${value}`;
}

/** A link in the body (not a reference chip: those open themselves). */
function linkTarget(event: MouseEvent): HTMLAnchorElement | null {
  const target = event.target instanceof Element ? event.target : null;
  const anchor = target?.closest("a[href]");
  if (!(anchor instanceof HTMLAnchorElement)) return null;
  if (anchor.closest('[contenteditable="false"]')) return null;
  return anchor;
}

const openExternal = (href: string) =>
  window.open(href, "_blank", "noopener,noreferrer");

/** While writing, a body link opens with Cmd / Ctrl + click (a plain click shows the card). */
export function openLinkOnModClick(event: MouseEvent): boolean {
  if (event.button !== 0 || !(event.metaKey || event.ctrlKey)) return false;
  const anchor = linkTarget(event);
  if (!anchor) return false;
  openExternal(anchor.href);
  return true;
}

/**
 * While reading (no focus in the body), a link opens on a plain click without
 * putting the caret in: stopped on mousedown (the focus does not move), opened
 * on the click that follows.
 */
export function linkMouseDownWhileReading(
  focused: boolean,
  event: MouseEvent,
): boolean {
  if (focused || event.button !== 0 || !linkTarget(event)) return false;
  event.preventDefault();
  return true;
}

export function linkClickWhileReading(
  focused: boolean,
  event: MouseEvent,
): boolean {
  const anchor = focused || event.button !== 0 ? null : linkTarget(event);
  if (!anchor) return false;
  event.preventDefault();
  openExternal(anchor.href);
  return true;
}

// ---- placement -------------------------------------------------------------------

export interface Box {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Around the editor element: `clip` is what is visible (the screen cut by every
 * clipping ancestor), `bounds` where the bar may go (the screen, minus what is
 * above the top / below the bottom of an ancestor whose content is scrolled
 * there: above a scrolled box's edge the bar floated outside the box).
 */
export function editorBoxesOf(el: Element): { clip: Box; bounds: Box } {
  const screen = {
    top: 0,
    left: 0,
    bottom: window.innerHeight || 0,
    right: window.innerWidth || 0,
  };
  const clip = { ...screen };
  const bounds = { ...screen };
  for (let p: Element | null = el; p; p = p.parentElement) {
    const style = getComputedStyle(p);
    const clipsY = !!style.overflowY && style.overflowY !== "visible";
    const clipsX = !!style.overflowX && style.overflowX !== "visible";
    if (!clipsY && !clipsX) continue;
    const r = p.getBoundingClientRect();
    if (clipsY) {
      clip.top = Math.max(clip.top, r.top);
      clip.bottom = Math.min(clip.bottom, r.bottom);
      if (p.scrollTop > 0) bounds.top = Math.max(bounds.top, r.top);
      if (p.scrollTop + p.clientHeight < p.scrollHeight - 1)
        bounds.bottom = Math.min(bounds.bottom, r.bottom);
    }
    if (clipsX) {
      clip.left = Math.max(clip.left, r.left);
      clip.right = Math.min(clip.right, r.right);
    }
  }
  return { clip, bounds };
}

/** The anchor cut to what is visible; null when none of it is. */
export function visibleAnchor(anchor: Box, clip: Box): Box | null {
  const top = Math.max(anchor.top, clip.top);
  const bottom = Math.min(anchor.bottom, clip.bottom);
  if (bottom < top) return null;
  const left = Math.min(Math.max(anchor.left, clip.left), clip.right);
  const right = Math.max(Math.min(anchor.right, clip.right), left);
  return { top, bottom, left, right };
}

const GAP = 8;
const EDGE = 8;
/** The room between the body and what is above it (the status line under the title). */
const TITLE_GAP = 24;

/** Above the anchor inside `bounds` (below when there is no room), start-aligned. */
export function placeAbove(
  anchor: Box,
  bounds: Box,
  panel: { width: number; height: number },
): { top: number; left: number } {
  const above = anchor.top - GAP - panel.height;
  const top =
    above >= bounds.top + EDGE
      ? above
      : Math.min(anchor.bottom + GAP, bounds.bottom - EDGE - panel.height);
  const left = Math.max(
    bounds.left + EDGE,
    Math.min(anchor.left, bounds.right - EDGE - panel.width),
  );
  return { top: Math.round(top), left: Math.round(left) };
}

/**
 * In an app that draws the Dock (`data-phone-host`), the room from the
 * screen's bottom that what it draws there takes while no keyboard is up: its
 * Dock (48, at the home bar's room and 5 more) and, 12 above it, a page's
 * buttons (a document's undo, redo and outline, 44 high). None of it is an
 * element of the page. (The phone's own parts are not imported here.)
 */
const HOST_BOTTOM = "calc(var(--dock-floor) + 5px + 48px + 12px + 44px)";

/** `HOST_BOTTOM` in px, measured once per window size (the bar follows scrolling). */
let hostRoom: { size: string; px: number } | null = null;
function hostBottomRoom(): number {
  const size = `${window.innerWidth}x${window.innerHeight}`;
  if (hostRoom?.size === size) return hostRoom.px;
  const probe = document.createElement("div");
  probe.style.cssText = `position:fixed;visibility:hidden;pointer-events:none;height:${HOST_BOTTOM}`;
  document.body.append(probe);
  const px = probe.getBoundingClientRect().height;
  probe.remove();
  hostRoom = { size, px };
  return px;
}

/** On a phone: at the bottom of the visible screen, centered. */
function placeDocked(panel: { width: number; height: number }): {
  top: number;
  left: number;
} {
  const vv = window.visualViewport;
  let bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
  // Above the phone's Dock while it shows (it steps away while the keyboard
  // is up, and then the bottom of the visible screen is free).
  const dock = document.querySelector<HTMLElement>(
    "[data-phone-dock]:not([inert])",
  );
  const top = dock?.getBoundingClientRect().top;
  if (top !== undefined && top > 0) bottom = Math.min(bottom, top);
  // In an app the Dock and the page's buttons over it are the app's own, not
  // elements of the page: above the room they take (it lay behind them). With
  // the keyboard up they step away and the visible screen ends higher anyway.
  if (document.documentElement.hasAttribute("data-phone-host"))
    bottom = Math.min(bottom, window.innerHeight - hostBottomRoom());
  const width = window.innerWidth || 0;
  return {
    left: Math.max(EDGE, Math.round((width - panel.width) / 2)),
    top: Math.round(bottom - EDGE - panel.height),
  };
}

// ---- the bar ---------------------------------------------------------------------

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

export function SelectionMenu({ editor }: { editor: Editor }) {
  const menu = useEditorState({
    editor,
    selector: ({ editor: e }) => (e ? selectionMenuState(e) : null),
    equalityFn: same,
  });
  const card = useEditorState({
    editor,
    selector: ({ editor: e }) => (e ? caretLink(e) : null),
    equalityFn: same,
  });
  const reading = useEditorState({
    editor,
    selector: ({ editor: e }) => (e ? isReading(e) : false),
  });
  const [pointerDown, setPointerDown] = useState(false);
  // The body was last moved by the mouse (keys make it false): the card at a
  // link's edge shows only after a click.
  const [viaPointer, setViaPointer] = useState(false);
  const [focused, setFocused] = useState(false);
  const [linkDraft, setLinkDraft] = useState<string | null>(null);
  const [typeAnchor, setTypeAnchor] = useState<DOMRect | null>(null);
  const bar = useRef<HTMLDivElement>(null);
  const typeButton = useRef<HTMLButtonElement>(null);
  const [placed, setPlaced] = useState<{ top: number; left: number } | null>(
    null,
  );

  // frontend-development: not a fetch; follows the mouse on the body.
  useEffect(() => {
    const dom = editor.view.dom;
    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      setPointerDown(true);
      setViaPointer(true);
    };
    // Released maybe outside the body (dragging the selection out).
    const onUp = () => setPointerDown(false);
    const onKey = () => setViaPointer(false);
    dom.addEventListener("mousedown", onDown);
    dom.addEventListener("keydown", onKey);
    window.addEventListener("mouseup", onUp);
    return () => {
      dom.removeEventListener("mousedown", onDown);
      dom.removeEventListener("keydown", onKey);
      window.removeEventListener("mouseup", onUp);
    };
  }, [editor]);

  // frontend-development: not a fetch; whether the focus is in the body or this bar.
  useEffect(() => {
    const sync = () => {
      const active = document.activeElement;
      setFocused(
        editor.view.hasFocus() ||
          (active !== null && (bar.current?.contains(active) ?? false)),
      );
    };
    sync();
    // On blur the next focus may not be settled yet.
    const onBlur = () => setTimeout(sync, 0);
    editor.on("focus", sync);
    editor.on("blur", onBlur);
    return () => {
      editor.off("focus", sync);
      editor.off("blur", onBlur);
    };
  }, [editor]);

  const subOpen = linkDraft !== null || typeAnchor !== null;
  // A selection that went away (a remote edit removed it) drops the link field too.
  if (!menu && (linkDraft !== null || typeAnchor !== null)) {
    setLinkDraft(null);
    setTypeAnchor(null);
  }
  const showCard =
    menu === null && card !== null && (card.inside || viaPointer);
  const visible =
    (menu !== null || showCard) &&
    (subOpen || (focused && !pointerDown) || (reading && menu !== null));
  const range = useRef<{ from: number; to: number } | null>(null);
  range.current = menu === null && card ? card : null;

  const measure = useCallback(() => {
    const el = bar.current;
    if (!el) return;
    const { from, to } = range.current ?? editor.state.selection;
    const anchor = posToDOMRect(editor.view, from, to);
    const { clip, bounds } = editorBoxesOf(editor.view.dom);
    const seen = visibleAnchor(anchor, clip);
    // Out of sight: hide (but keep the field that is being typed in).
    if (!seen && !el.contains(document.activeElement)) {
      setPlaced(null);
      return;
    }
    const panel = { width: el.offsetWidth, height: el.offsetHeight };
    // Not over what stands above the body (the title): placed above the first
    // lines, it covered half of the title; it goes below them instead.
    const above = {
      ...bounds,
      top: Math.max(
        bounds.top,
        editor.view.dom.getBoundingClientRect().top - TITLE_GAP,
      ),
    };
    const next = touchFirst()
      ? placeDocked(panel)
      : placeAbove(seen ?? anchor, above, panel);
    setPlaced((prev) =>
      prev && prev.top === next.top && prev.left === next.left ? prev : next,
    );
  }, [editor]);

  // frontend-development: not a fetch; places the bar and follows the selection and scrolling.
  useLayoutEffect(() => {
    if (!visible) {
      setPlaced(null);
      return;
    }
    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    if (bar.current) observer?.observe(bar.current);
    editor.on("selectionUpdate", measure);
    editor.on("update", measure);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", measure);
    vv?.addEventListener("scroll", measure);
    return () => {
      observer?.disconnect();
      editor.off("selectionUpdate", measure);
      editor.off("update", measure);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      vv?.removeEventListener("resize", measure);
      vv?.removeEventListener("scroll", measure);
    };
  }, [visible, editor, measure]);

  if (!visible) return null;
  const style = placed
    ? { top: placed.top, left: placed.left }
    : { top: 0, left: 0, visibility: "hidden" as const };
  const frame = `fixed z-50 flex items-center gap-px overflow-visible px-[5px] py-[4px] ${POPUP_PANEL}`;

  if (menu === null) {
    if (!card) return null;
    return (
      <div
        ref={bar}
        role="toolbar"
        aria-label={m.documents_selection_link()}
        style={style}
        className={frame}
      >
        <input
          // Another link: new contents (uncontrolled, made again by key).
          key={`${card.from}:${card.href}`}
          type="text"
          aria-label={m.documents_selection_link_url()}
          defaultValue={card.href}
          onKeyDown={(e) => {
            if (isImeComposing(e.nativeEvent)) return;
            if (e.key === "Enter") {
              e.preventDefault();
              const href = normalizeHref(e.currentTarget.value);
              if (href === null) removeLinkRange(editor, card);
              else
                editor
                  .chain()
                  .focus()
                  .setTextSelection({ from: card.from, to: card.to })
                  .setLink({ href })
                  .setTextSelection(card.to)
                  .run();
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              editor.commands.focus();
            }
          }}
          className="plain-field h-7 w-56 truncate border-none bg-transparent px-2 text-[13px] text-body outline-none"
        />
        <span
          aria-hidden="true"
          className="mx-1 h-5 w-px flex-none bg-border"
        />
        <BarButton
          label={m.documents_selection_open_link({
            key: isMac() ? "⌘" : "Ctrl",
          })}
          active={false}
          onPress={() => openExternal(card.href)}
        >
          <OpenGlyph />
        </BarButton>
        <BarButton
          label={m.documents_selection_unlink()}
          active={false}
          onPress={() => removeLinkRange(editor, card)}
        >
          <UnlinkGlyph />
        </BarButton>
      </div>
    );
  }

  const toggle = (name: "bold" | "italic" | "strike" | "code") => {
    const chain = editor.chain().focus();
    if (name === "bold") chain.toggleBold();
    else if (name === "italic") chain.toggleItalic();
    else if (name === "strike") chain.toggleStrike();
    else chain.toggleCode();
    chain.run();
  };
  const applyLink = () => {
    const href = normalizeHref(linkDraft ?? "");
    if (href === null) removeLinks(editor);
    else editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    setLinkDraft(null);
  };
  const unlink = () => {
    removeLinks(editor);
    setLinkDraft(null);
  };

  return (
    <>
      <div
        ref={bar}
        role="toolbar"
        aria-label={m.documents_selection_format()}
        style={style}
        className={frame}
      >
        {linkDraft !== null && (
          <input
            // biome-ignore lint/a11y/noAutofocus: opened by the link button to type the URL at once
            autoFocus
            type="text"
            aria-label={m.documents_selection_link_url()}
            placeholder={m.documents_selection_paste_url()}
            value={linkDraft}
            onChange={(e) => setLinkDraft(e.target.value)}
            onKeyDown={(e) => {
              if (isImeComposing(e.nativeEvent)) return;
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              } else if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setLinkDraft(null);
                editor.commands.focus();
              }
            }}
            onBlur={(e) => {
              if (!bar.current?.contains(e.relatedTarget as Node | null))
                setLinkDraft(null);
            }}
            // The panel's frame is the field's edge: no second frame, no ring.
            className="plain-field h-7 w-64 border-none bg-transparent px-2 text-[13px] text-body outline-none"
          />
        )}
        {linkDraft !== null && menu.link && (
          <BarButton
            label={m.documents_selection_unlink()}
            active={false}
            onPress={unlink}
          >
            <UnlinkGlyph />
          </BarButton>
        )}
        {linkDraft === null && (
          <>
            {menu.textType !== null && (
              <BarButton
                label={m.documents_block_menu_section_type()}
                buttonRef={typeButton}
                active={typeAnchor !== null}
                wide
                onPress={() => {
                  const rect = typeButton.current?.getBoundingClientRect();
                  if (rect) setTypeAnchor(rect);
                }}
              >
                <span className="text-[13px]">Aa</span>
                <ChevronDown />
              </BarButton>
            )}
            <BarButton
              label={m.documents_selection_bold({ key: keyLabel("mod+b")[0] })}
              active={menu.bold}
              onPress={() => toggle("bold")}
            >
              <span className="font-bold">B</span>
            </BarButton>
            <BarButton
              label={m.documents_selection_italic({
                key: keyLabel("mod+i")[0],
              })}
              active={menu.italic}
              onPress={() => toggle("italic")}
            >
              <span className="font-serif italic">I</span>
            </BarButton>
            <BarButton
              label={m.documents_selection_strike({
                key: keyLabel("mod+shift+s")[0],
              })}
              active={menu.strike}
              onPress={() => toggle("strike")}
            >
              <span className="line-through">S</span>
            </BarButton>
            <BarButton
              label={m.documents_selection_code({ key: keyLabel("mod+e")[0] })}
              active={menu.code}
              onPress={() => toggle("code")}
            >
              <CodeGlyph />
            </BarButton>
            <BarButton
              label={
                menu.link
                  ? m.documents_selection_edit_link()
                  : m.documents_selection_link()
              }
              active={menu.link}
              onPress={() => setLinkDraft(linkRange(editor)?.href ?? "")}
            >
              <Icon kind="link" size={15} />
            </BarButton>
            {menu.link && (
              <BarButton
                label={m.documents_selection_unlink()}
                active={false}
                onPress={unlink}
              >
                <UnlinkGlyph />
              </BarButton>
            )}
          </>
        )}
      </div>
      {typeAnchor !== null && menu.textType !== null && (
        <FloatingMenu
          anchor={typeAnchor}
          placement="below"
          label={m.documents_block_menu_section_type()}
          items={TEXT_TYPES.map((t) => ({
            id: t.id,
            label: t.label(),
            selected: t.id === menu.textType,
            onSelect: () => {
              setTypeAnchor(null);
              editor
                .chain()
                .focus()
                .setNode(
                  t.name,
                  t.level === undefined ? {} : { level: t.level },
                )
                .run();
            },
          }))}
          onClose={() => {
            editor.commands.focus();
            setTypeAnchor(null);
          }}
        />
      )}
    </>
  );
}

/** One button of the bar: pressed is the background and the text color, never bold. */
function BarButton({
  label,
  active,
  onPress,
  buttonRef,
  wide = false,
  children,
}: {
  label: string;
  active: boolean;
  onPress(): void;
  buttonRef?: Ref<HTMLButtonElement>;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip text={label}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-pressed={active}
        // The body keeps its selection and focus. The action runs on click: a menu
        // opened on mousedown would see that same mousedown as a press outside.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onPress}
        // Finger-sized on touch screens (28px made the neighbour easy to hit).
        className={`flex h-7 flex-none cursor-pointer items-center justify-center gap-[3px] rounded-[6px] text-[14px] leading-none pointer-coarse:h-9 ${
          wide ? "px-2" : "w-7 pointer-coarse:w-9"
        } ${active ? "bg-hover text-accent" : "text-body hover:bg-hover"}`}
      >
        <span className="pointer-events-none flex items-center gap-[3px]">
          {children}
        </span>
      </button>
    </Tooltip>
  );
}

/** A 16-grid line glyph in the icon set's stroke (decoration: the button has the name). */
function Glyph({ d, size = 16 }: { d: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

const ChevronDown = () => <Glyph d="M4 6l4 4 4-4" size={10} />;
const CodeGlyph = () => (
  <Glyph d="M5.5 4.5L2 8l3.5 3.5M10.5 4.5L14 8l-3.5 3.5M9 3.5l-2 9" />
);
/** The link's two rings apart, with short strokes at the cut. */
const UnlinkGlyph = () => (
  <Glyph d="M10 8.5l2.5-2.5A2.5 2.5 0 009 2.5L7.8 3.7M6 7.5L3.5 10A2.5 2.5 0 007 13.5l1.2-1.2M2.5 4.5l1.5.5M4.5 2.5l.5 1.5M13.5 11.5l-1.5-.5M11.5 13.5l-.5-1.5" />
);
/** Opens elsewhere: an arrow leaving a box. */
const OpenGlyph = () => (
  <Glyph d="M9.5 2.5h4v4M13.5 2.5L7.5 8.5M12 9.5V13a.5.5 0 01-.5.5h-8A.5.5 0 013 13V5a.5.5 0 01.5-.5H7" />
);
